import { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { X, LogOut, RefreshCw, Filter, Search, ExternalLink, Package, ShoppingBag, Shirt, Car } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const STORAGE_KEY = 'impre_admin_pwd';
// TODO(security P1): Migrar de localStorage a cookie HTTP-only firmada por backend
// para eliminar exposición XSS. Requiere endpoint /api/admin/session que emita cookie
// y middleware server-side que valide el session token en cada request.

const devError = (...args) => {
  if (process.env.NODE_ENV !== 'production') {
    // eslint-disable-next-line no-console
    console.error(...args);
  }
};

export default function AdminPanel() {
  const [password, setPassword] = useState(() => localStorage.getItem(STORAGE_KEY) || '');
  const [authenticated, setAuthenticated] = useState(false);
  const [loginError, setLoginError] = useState('');
  const [orders, setOrders] = useState([]);
  const [counts, setCounts] = useState({ combo: 0, bags: 0, shirts: 0, alfombras: 0, carts: 0, total: 0 });
  const [loading, setLoading] = useState(false);
  const [filterType, setFilterType] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [search, setSearch] = useState('');
  const [selectedOrder, setSelectedOrder] = useState(null);

  const loadOrders = useCallback(async (pwd, silentLogin = false) => {
    setLoading(true);
    try {
      const res = await axios.post(`${API}/admin/all-orders`, { password: pwd });
      setOrders(res.data.orders);
      setCounts(res.data.counts);
      setAuthenticated(true);
    } catch (err) {
      if (silentLogin) {
        localStorage.removeItem(STORAGE_KEY);
      } else {
        setLoginError('Error al cargar pedidos');
      }
    } finally {
      setLoading(false);
    }
  }, []);

  // Auto-login si hay contraseña guardada (solo al montar)
  useEffect(() => {
    if (password) {
      loadOrders(password, true);
    }
    // Intentional: run only once on mount; password is read from initial localStorage
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadOrders]);

  const handleLogin = async (e) => {
    e && e.preventDefault();
    setLoginError('');
    try {
      await axios.post(`${API}/admin/verify`, { password });
      localStorage.setItem(STORAGE_KEY, password);
      setAuthenticated(true);
      loadOrders(password);
    } catch (err) {
      devError('Admin login failed', err);
      setLoginError('Contraseña incorrecta');
      localStorage.removeItem(STORAGE_KEY);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem(STORAGE_KEY);
    setAuthenticated(false);
    setPassword('');
    setOrders([]);
  };

  const updateStatus = async (order, newStatus) => {
    try {
      await axios.post(`${API}/admin/update-status`, {
        password,
        code: order.code,
        type: order.type,
        status: newStatus
      });
      setOrders(prev => prev.map(o => o.code === order.code ? { ...o, status: newStatus } : o));
      if (selectedOrder && selectedOrder.code === order.code) {
        setSelectedOrder({ ...selectedOrder, status: newStatus });
      }
    } catch (err) {
      devError('updateStatus failed', err);
      alert('Error al actualizar estado');
    }
  };

  const filtered = orders.filter(o => {
    if (filterType !== 'all' && o.type !== filterType) return false;
    if (filterStatus !== 'all' && (o.status || 'pending') !== filterStatus) return false;
    if (search) {
      const q = search.toLowerCase();
      const hay = `${o.code} ${o.client_name || ''} ${o.client_phone || ''} ${o.company_name || ''}`.toLowerCase();
      if (!hay.includes(q)) return false;
    }
    return true;
  });

  const typeConfig = {
    combo: { label: 'Combo', icon: Package, color: 'bg-purple-100 text-purple-800 border-purple-300' },
    bag: { label: 'Bolsa', icon: ShoppingBag, color: 'bg-blue-100 text-blue-800 border-blue-300' },
    shirt: { label: 'Camiseta/Pulsera', icon: Shirt, color: 'bg-pink-100 text-pink-800 border-pink-300' },
    alfombra: { label: 'Alfombra', icon: Car, color: 'bg-cyan-100 text-cyan-800 border-cyan-300' },
    cart: { label: 'Carrito', icon: ShoppingBag, color: 'bg-amber-100 text-amber-800 border-amber-300' }
  };

  const statusConfig = {
    pending: { label: 'Pendiente', color: 'bg-amber-100 text-amber-800 border-amber-300' },
    confirmed: { label: 'Confirmado', color: 'bg-cyan-100 text-cyan-800 border-cyan-300' },
    producing: { label: 'En Producción', color: 'bg-blue-100 text-blue-800 border-blue-300' },
    shipped: { label: 'Enviado', color: 'bg-indigo-100 text-indigo-800 border-indigo-300' },
    completed: { label: 'Completado', color: 'bg-emerald-100 text-emerald-800 border-emerald-300' },
    cancelled: { label: 'Cancelado', color: 'bg-red-100 text-red-800 border-red-300' }
  };

  const formatDate = (iso) => {
    if (!iso) return '—';
    try {
      const d = new Date(iso);
      return d.toLocaleString('es-VE', { year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' });
    } catch { return iso; }
  };

  // Cálculo total ingresos
  const totalIncome = filtered.reduce((sum, o) => sum + (o.price_usd || o.priceUsd || 0), 0);

  // LOGIN VIEW
  if (!authenticated) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center p-4">
        <div className="max-w-md w-full">
          <div className="text-center mb-8">
            <img src="/logo-impreplastico.png" alt="Impreplastico" className="h-14 mx-auto mb-4 bg-white rounded-xl p-2" />
            <h1 className="text-3xl font-black text-white">Panel Admin</h1>
            <p className="text-slate-400 text-sm mt-2">Acceso restringido</p>
          </div>
          <form onSubmit={handleLogin} className="bg-white rounded-2xl p-6 shadow-2xl space-y-4">
            <div>
              <label className="block text-sm font-bold text-slate-700 mb-1.5">Contraseña</label>
              <input
                type="password"
                value={password}
                onChange={e => setPassword(e.target.value)}
                data-testid="admin-password"
                placeholder="•••••••••"
                autoFocus
                className="w-full bg-slate-50 border-2 border-slate-200 rounded-xl px-4 py-3 text-slate-800 focus:border-slate-500 focus:bg-white focus:outline-none transition"
              />
            </div>
            {loginError && <p className="text-red-600 text-sm font-semibold bg-red-50 p-2 rounded-lg">{loginError}</p>}
            <button
              type="submit"
              data-testid="admin-login-btn"
              className="w-full bg-slate-900 hover:bg-slate-800 text-white py-3 rounded-xl font-black shadow-lg"
            >
              INGRESAR
            </button>
          </form>
        </div>
      </div>
    );
  }

  // ADMIN DASHBOARD
  return (
    <div className="min-h-screen bg-slate-100">
      {/* Header */}
      <header className="bg-slate-900 text-white shadow-lg">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="/logo-impreplastico.png" alt="" className="h-10 bg-white rounded-lg p-1" />
            <div>
              <h1 className="text-xl font-black">Panel Admin</h1>
              <p className="text-xs text-slate-400">Impreplástico · Gestión de Pedidos</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={() => loadOrders(password)} disabled={loading} className="p-2 rounded-lg hover:bg-white/10 transition" title="Actualizar">
              <RefreshCw className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
            </button>
            <button onClick={handleLogout} data-testid="admin-logout" className="flex items-center gap-2 bg-white/10 hover:bg-white/20 px-3 py-2 rounded-lg text-sm font-semibold transition">
              <LogOut className="w-4 h-4" /> Salir
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto p-4 space-y-4">
        {/* Stats cards */}
        <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
          <StatCard label="Total pedidos" value={counts.total} color="bg-slate-900 text-white" />
          <StatCard label="Carritos" value={counts.carts} color="bg-amber-600 text-white" />
          <StatCard label="Combos" value={counts.combo} color="bg-purple-600 text-white" />
          <StatCard label="Bolsas" value={counts.bags} color="bg-blue-600 text-white" />
          <StatCard label="Camisetas/Pulseras" value={counts.shirts} color="bg-pink-600 text-white" />
          <StatCard label="Alfombras" value={counts.alfombras} color="bg-cyan-600 text-white" />
        </div>

        {/* Ingresos totales filtrados */}
        <div className="bg-emerald-500 text-white rounded-2xl p-5 shadow-lg">
          <p className="text-xs uppercase tracking-widest opacity-80">Ingresos (según filtros)</p>
          <p className="text-4xl font-black tabular-nums">${totalIncome.toFixed(2)} USD</p>
          <p className="text-sm opacity-90 mt-1">{filtered.length} pedido{filtered.length !== 1 && 's'} · promedio ${filtered.length ? (totalIncome / filtered.length).toFixed(2) : '0.00'} c/u</p>
        </div>

        {/* Filtros */}
        <div className="bg-white rounded-2xl p-4 shadow-md space-y-3">
          <div className="flex items-center gap-2 text-slate-700 font-bold">
            <Filter className="w-4 h-4" /> Filtros
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div>
              <label className="block text-xs font-bold text-slate-600 mb-1">Tipo</label>
              <select value={filterType} onChange={e => setFilterType(e.target.value)} className="w-full bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 text-sm">
                <option value="all">Todos</option>
                <option value="combo">Combos</option>
                <option value="bag">Bolsas</option>
                <option value="shirt">Camisetas/Pulseras</option>
                <option value="alfombra">Alfombras</option>
                <option value="cart">Carritos</option>
              </select>
            </div>
            <div>
              <label className="block text-xs font-bold text-slate-600 mb-1">Estado</label>
              <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)} className="w-full bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 text-sm">
                <option value="all">Todos</option>
                {Object.entries(statusConfig).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-bold text-slate-600 mb-1">Buscar</label>
              <div className="relative">
                <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Código, nombre, teléfono..." className="w-full bg-slate-50 border border-slate-200 rounded-lg pl-9 pr-3 py-2 text-sm" />
              </div>
            </div>
          </div>
        </div>

        {/* Tabla */}
        <div className="bg-white rounded-2xl shadow-md overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-100 text-slate-700 text-xs uppercase font-bold">
                <tr>
                  <th className="px-3 py-3 text-left">Código</th>
                  <th className="px-3 py-3 text-left">Tipo</th>
                  <th className="px-3 py-3 text-left">Cliente</th>
                  <th className="px-3 py-3 text-left">Monto</th>
                  <th className="px-3 py-3 text-left">Fecha</th>
                  <th className="px-3 py-3 text-left">Estado</th>
                  <th className="px-3 py-3 text-left">Ver</th>
                </tr>
              </thead>
              <tbody>
                {filtered.length === 0 && (
                  <tr><td colSpan={7} className="text-center text-slate-500 py-8">Sin pedidos que coincidan con los filtros</td></tr>
                )}
                {filtered.map(o => {
                  const Tcfg = typeConfig[o.type] || typeConfig.bag;
                  const Scfg = statusConfig[o.status || 'pending'] || statusConfig.pending;
                  const TIcon = Tcfg.icon;
                  return (
                    <tr key={o.code} className="border-t border-slate-100 hover:bg-slate-50">
                      <td className="px-3 py-3 font-mono font-bold text-xs">{o.code}</td>
                      <td className="px-3 py-3">
                        <span className={`inline-flex items-center gap-1 text-xs font-bold px-2 py-1 rounded-full border ${Tcfg.color}`}>
                          <TIcon className="w-3 h-3" /> {Tcfg.label}
                        </span>
                      </td>
                      <td className="px-3 py-3">
                        <p className="font-bold text-slate-800">{o.client_name || '—'}</p>
                        <p className="text-xs text-slate-500">{o.client_phone || '—'}</p>
                      </td>
                      <td className="px-3 py-3">
                        <p className="font-bold text-slate-800">${(o.price_usd || 0).toFixed(2)}</p>
                        <p className="text-xs text-emerald-600 font-semibold">Bs. {(o.price_bs || 0).toFixed(0)}</p>
                      </td>
                      <td className="px-3 py-3 text-xs text-slate-600">{formatDate(o.timestamp)}</td>
                      <td className="px-3 py-3">
                        <span className={`inline-block text-xs font-bold px-2 py-1 rounded-full border ${Scfg.color}`}>
                          {Scfg.label}
                        </span>
                      </td>
                      <td className="px-3 py-3">
                        <button onClick={() => setSelectedOrder(o)} data-testid={`view-${o.code}`} className="text-slate-700 hover:text-slate-900 underline text-xs font-bold">
                          Ver
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </main>

      {/* Modal detalle */}
      {selectedOrder && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => setSelectedOrder(null)}>
          <div onClick={e => e.stopPropagation()} className="bg-white rounded-3xl max-w-xl w-full max-h-[92vh] overflow-y-auto shadow-2xl">
            <div className="sticky top-0 bg-slate-900 text-white p-4 flex justify-between items-center">
              <div>
                <p className="text-xs text-slate-400 uppercase font-bold">Pedido</p>
                <p className="font-mono font-black">{selectedOrder.code}</p>
              </div>
              <button onClick={() => setSelectedOrder(null)} className="hover:bg-white/10 p-2 rounded-full"><X className="w-5 h-5" /></button>
            </div>
            <div className="p-5 space-y-4">
              {/* Datos del cliente */}
              <div className="bg-slate-50 rounded-xl p-4 space-y-1 text-sm">
                <p><strong>Cliente:</strong> {selectedOrder.client_name}</p>
                <p><strong>WhatsApp:</strong> <a href={`https://wa.me/${(selectedOrder.client_phone || '').replace(/\D/g, '')}`} target="_blank" rel="noopener noreferrer" className="text-emerald-600 underline">{selectedOrder.client_phone}</a></p>
                <p><strong>Empresa:</strong> {selectedOrder.company_name || selectedOrder.business_name || '—'}</p>
                {selectedOrder.type === 'alfombra' ? (
                  <p><strong>Ciudad:</strong> {selectedOrder.city || '—'}</p>
                ) : (
                  <p><strong>MRW:</strong> {selectedOrder.mrw_address || '—'}</p>
                )}
              </div>

              {/* Producto */}
              <div className="bg-slate-50 rounded-xl p-4 space-y-1 text-sm">
                <p className="font-bold text-slate-700 mb-1">Producto:</p>
                {selectedOrder.type === 'combo' && <p>Combo S+M · 200 unidades</p>}
                {selectedOrder.type === 'bag' && (
                  <>
                    <p><strong>{selectedOrder.product_name}</strong></p>
                    <p>Cantidad: {selectedOrder.quantity} · Colores: {selectedOrder.print_colors}</p>
                    <p>Precio unitario: ${(selectedOrder.unit_price_usd || 0).toFixed(2)}</p>
                  </>
                )}
                {selectedOrder.type === 'shirt' && (
                  <p><strong>{selectedOrder.country || selectedOrder.product_name || 'Camiseta/Pulsera'}</strong> {selectedOrder.size ? `· Talla ${selectedOrder.size}` : ''}</p>
                )}
                {selectedOrder.type === 'alfombra' && (
                  <>
                    <p><strong>Alfombras para Carro Personalizadas</strong></p>
                    <p>Cantidad comprada: <strong>{selectedOrder.quantity}</strong> {selectedOrder.bonus > 0 && <span className="text-emerald-700 font-bold">+ {selectedOrder.bonus} GRATIS</span>}</p>
                    <p>Total a recibir: <strong>{selectedOrder.total_units}</strong></p>
                    <p>Impresión: <strong>{selectedOrder.print_colors || 1} color{(selectedOrder.print_colors || 1) > 1 ? 'es' : ''}</strong></p>
                    <p>Propuesta digital: <strong>{selectedOrder.digital_proposal_requested ? 'Sí' : 'No'}</strong></p>
                  </>
                )}
                {selectedOrder.type === 'cart' && (
                  <>
                    <p><strong>Carrito multi-bolsa</strong> · {selectedOrder.total_units} unidades totales</p>
                    <div className="mt-2 space-y-1">
                      {(selectedOrder.items || []).map((it, i) => (
                        <p key={`${it.size_key || it.product_name}-${it.print_colors}-${i}`} className="text-xs bg-white p-2 rounded border border-slate-200">
                          <strong>{it.quantity}×</strong> {it.product_name} · {it.print_colors} color{it.print_colors > 1 ? 'es' : ''} · ${it.subtotal_usd?.toFixed(2)}
                        </p>
                      ))}
                    </div>
                  </>
                )}
              </div>

              {/* Pago */}
              <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4 text-sm">
                <p className="text-emerald-800"><strong>Monto:</strong> ${(selectedOrder.price_usd || 0).toFixed(2)} USD / Bs. {(selectedOrder.price_bs || 0).toFixed(2)}</p>
                <p className="text-emerald-700 text-xs mt-1">Tasa BCV al momento del pedido: {selectedOrder.tasa_bcv}</p>
              </div>

              {/* Comprobante */}
              {selectedOrder.receipt_url && (
                <a href={`${BACKEND_URL}${selectedOrder.receipt_url}`} target="_blank" rel="noopener noreferrer" className="block bg-cyan-50 border border-cyan-200 rounded-xl p-4 hover:bg-cyan-100 transition">
                  <p className="text-cyan-800 font-bold flex items-center gap-2">📸 Ver comprobante de pago <ExternalLink className="w-4 h-4" /></p>
                </a>
              )}

              {/* Cambiar estado */}
              <div>
                <p className="text-sm font-bold text-slate-700 mb-2">Cambiar estado:</p>
                <div className="grid grid-cols-3 gap-2">
                  {Object.entries(statusConfig).map(([key, cfg]) => (
                    <button
                      key={key}
                      onClick={() => updateStatus(selectedOrder, key)}
                      className={`px-3 py-2 rounded-lg text-xs font-bold border-2 transition ${(selectedOrder.status || 'pending') === key ? cfg.color + ' ring-2 ring-slate-400' : 'bg-white border-slate-200 text-slate-600 hover:bg-slate-50'}`}
                    >
                      {cfg.label}
                    </button>
                  ))}
                </div>
              </div>

              <p className="text-xs text-slate-500 text-center">Creado: {formatDate(selectedOrder.timestamp)}</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function StatCard({ label, value, color }) {
  return (
    <div className={`rounded-2xl p-4 shadow-md ${color}`}>
      <p className="text-xs uppercase tracking-widest opacity-80">{label}</p>
      <p className="text-3xl font-black tabular-nums mt-1">{value}</p>
    </div>
  );
}
